import React, { useEffect, useRef, useState } from 'react';
import { getRandomWord } from '../data/wordLibrary';
import orheartImage from '../orheart.webp';
import friendsImage from '../friends.webp';
import AgeWheel, { AGE_WHEEL_DEFAULT } from './AgeWheel';
import './PersonalityChart.css';

const PERSONALITY_DESCRIPTORS = {
  Perfectionist: 'Principled, purposeful, self-controlled, and perfectionistic.',
  Helper: 'Generous, demonstrative, people-pleasing, and possessive.',
  Achiever: 'Adaptable, excelling, driven, and image-conscious.',
  Individualist: 'Expressive, dramatic, self-absorbed, and temperamental.',
  Investigator: 'Perceptive, innovative, secretive, and isolated.',
  Loyalist: 'Engaging, responsible, anxious, and suspicious.',
  Enthusiast: 'Spontaneous, versatile, acquisitive, and scattered.',
  Challenger: 'Self-confident, decisive, willful, and confrontational.',
  Peacemaker: 'Receptive, reassuring, complacent, and resigned.',
};

const PERSONALITY_KEYS = Object.keys(PERSONALITY_DESCRIPTORS);

export { PERSONALITY_DESCRIPTORS };

const PersonalityChart = ({
  dominantPersonality,
  onDominantChange,
  onClose,
  titleDescriptor,
  onTitleDescriptorChange,
  inline = false,
  wordFromJungianAfterConfirm = false,
  hidePersonalityRing = false,
  generatedWordReady = false,
  onPersonalityConfirmed,
  collageFinderEnabled = false,
  finderSelfGender = null,
  onFinderSelfGenderChange,
  finderFriendLuv = false,
  finderRomanticLuv = false,
  onFinderFriendLuvChange,
  onFinderRomanticLuvChange,
  /** Romantic match prefs (multiple): `male` | `female` | `nb` — same as FoxChat `lookingForGenders`. */
  finderLookingForGenders = [],
  onFinderLookingForGendersChange,
  finderSelfAge = AGE_WHEEL_DEFAULT,
  onFinderSelfAgeChange,
  /** Collage-a-friend: section labels use YOUR FRIEND'S instead of YOUR. */
  collageFriendFlow = false,
}) => {
  const yourBlockLabel = (rest) => (collageFriendFlow ? `YOUR FRIEND'S ${rest}` : `YOUR ${rest}`);
  const [ringOverDominant, setRingOverDominant] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [wordConfirmed, setWordConfirmed] = useState(false);
  const [showEmptyHint, setShowEmptyHint] = useState(false);
  const [inputShake, setInputShake] = useState(false);
  const descriptorInputRef = useRef(null);
  const [state, setState] = useState(() => {
    const validDominant = dominantPersonality && PERSONALITY_KEYS.includes(dominantPersonality)
      ? dominantPersonality
      : null;
    const dominant = validDominant || PERSONALITY_KEYS[Math.floor(Math.random() * PERSONALITY_KEYS.length)];
    const ring = PERSONALITY_KEYS.filter(k => k !== dominant).sort(() => Math.random() - 0.5);
    return { dominantKey: dominant, ringKeys: ring };
  });
  const { dominantKey, ringKeys } = state;

  // Sync initial dominant (including fallback random) to parent so it persists on reopen before Confirm
  useEffect(() => {
    if (onDominantChange && dominantKey !== dominantPersonality) {
      onDominantChange(dominantKey);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount to persist fallback

  // When opened from survey, skip personality pick and auto-start word generation
  useEffect(() => {
    if (wordFromJungianAfterConfirm && onPersonalityConfirmed && !confirmed) {
      setConfirmed(true);
      onPersonalityConfirmed();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const handleSwitch = (clickedKey) => {
    setState(prev => {
      const clickedIndex = prev.ringKeys.indexOf(clickedKey);
      const newRing = [...prev.ringKeys.slice(0, clickedIndex), prev.dominantKey, ...prev.ringKeys.slice(clickedIndex + 1)];
      return { dominantKey: clickedKey, ringKeys: newRing };
    });
    setConfirmed(false);
    if (onDominantChange) onDominantChange(clickedKey);
  };

  const renderTile = (key, isDominant, onDominantSwitch, onConfirm, isRingOverDominant, isConfirmed = false) => {
    const description = PERSONALITY_DESCRIPTORS[key];
    const showDescription = isDominant;
    return (
      <div
        key={key}
        className={`personality-tile ${isDominant ? 'personality-tile-dominant' : 'personality-tile-inactive'}`}
      >
        <button
          type="button"
          className="personality-tile-button"
          onClick={() => {
            if (isDominant) return;
            handleSwitch(key);
            setRingOverDominant(false);
          }}
          aria-expanded={isDominant}
          aria-label={key}
        >
          {key}
        </button>
        {showDescription && (
          <div className="personality-tile-description">
            {description}
          </div>
        )}
        {isDominant && (
          <>
            {onConfirm && (
              <button
                type="button"
                className="personality-tile-dominant-confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isConfirmed) onConfirm();
                }}
                disabled={isConfirmed}
              >
                {isConfirmed ? 'Confirmed!' : 'YES'}
              </button>
            )}
            {onDominantSwitch && (
              <button
                type="button"
                className="personality-tile-dominant-switch"
                onClick={(e) => {
                  e.stopPropagation();
                  onDominantSwitch();
                }}
              >
                CHANGE
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const inner = (
    <div className={`personality-chart-container ${inline ? 'personality-chart-inline' : ''} ${(wordFromJungianAfterConfirm || hidePersonalityRing) ? 'personality-chart-word-only' : ''}`} onClick={(e) => e.stopPropagation()}>
      {!inline && !wordFromJungianAfterConfirm && !hidePersonalityRing && <h2 className="personality-chart-title">Their Personality</h2>}

        {!wordFromJungianAfterConfirm && !hidePersonalityRing && (
          <div className={`personality-tiles-ring ${ringOverDominant && !inline ? 'personality-tiles-ring-over-dominant' : ''} ${inline ? 'personality-tiles-ring-inline-only' : ''}`}>
            <div className="personality-tile-center">
              {renderTile(dominantKey, true, inline ? null : () => setRingOverDominant(prev => !prev), inline ? null : () => {
                setConfirmed(true);
                if (wordFromJungianAfterConfirm && onPersonalityConfirmed) onPersonalityConfirmed();
              }, false, confirmed)}
            </div>
            {!inline && ringKeys.map((key, index) => (
              <div
                key={key}
                className="personality-tile-ring-item"
                style={{ '--i': index }}
              >
                {renderTile(key, false, null, null, ringOverDominant, false)}
              </div>
            ))}
            {!inline && ringOverDominant && (
              <button
                type="button"
                className="personality-tiles-ring-center-hit"
                onClick={() => setRingOverDominant(false)}
                aria-label="Bring center forward"
              />
            )}
          </div>
        )}

        {!inline && wordFromJungianAfterConfirm && confirmed && !generatedWordReady && (
          <div className="personality-chart-picking-word">
            {collageFriendFlow ? "Picking your friend's nickname..." : 'Picking your nickname...'}
          </div>
        )}

        {!inline && titleDescriptor != null && (!wordFromJungianAfterConfirm || generatedWordReady) && (
          <div className="personality-chart-title-descriptor-wrap">
            <span className="personality-chart-title-descriptor-label">{yourBlockLabel('NICKNAME')}</span>
            <div className="personality-chart-title-descriptor-input-wrap">
              <input
                ref={descriptorInputRef}
                type="text"
                className={`personality-chart-title-descriptor-input${inputShake ? ' personality-chart-title-descriptor-input-shake' : ''}`}
                value={titleDescriptor}
                onChange={(e) => {
                  setShowEmptyHint(false);
                  onTitleDescriptorChange && onTitleDescriptorChange(e.target.value);
                }}
                aria-label={collageFriendFlow ? "Edit your friend's nickname" : 'Edit your nickname'}
                aria-invalid={showEmptyHint}
              />
              {onTitleDescriptorChange && (
                <button
                  type="button"
                  className="personality-chart-title-descriptor-refresh"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const word = getRandomWord();
                    onTitleDescriptorChange(word);
                    setShowEmptyHint(false);
                  }}
                  aria-label="Pick a new random word"
                >
                  ↻
                </button>
              )}
            </div>
            {showEmptyHint && (
              <p className="personality-chart-title-descriptor-empty-hint" role="alert">
                {collageFriendFlow ? "Your friend's nickname can't be empty." : "Your nickname can't be empty."}
              </p>
            )}
          </div>
        )}

        {!inline &&
          collageFinderEnabled &&
          titleDescriptor != null &&
          (!wordFromJungianAfterConfirm || generatedWordReady) && (
            <>
              <div className="personality-chart-finder-section">
                <span className="personality-chart-title-descriptor-label">{yourBlockLabel('GENDER')}</span>
                <div className="personality-chart-finder-gender-row">
                  {(['male', 'female', 'nb']).map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`personality-chart-finder-gender-btn${finderSelfGender === g ? ' personality-chart-finder-gender-btn--selected' : ''}`}
                      onClick={() => onFinderSelfGenderChange && onFinderSelfGenderChange(g)}
                    >
                      {g === 'male' ? 'MALE' : g === 'female' ? 'FEMALE' : 'NB'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="personality-chart-finder-section personality-chart-finder-section--age">
                <span className="personality-chart-title-descriptor-label">{yourBlockLabel('AGE')}</span>
                <div className="personality-chart-finder-age-wheel-wrap">
                  <AgeWheel
                    value={finderSelfAge ?? AGE_WHEEL_DEFAULT}
                    onChange={(n) => onFinderSelfAgeChange && onFinderSelfAgeChange(n)}
                    title={null}
                    visibleRowCount={3}
                  />
                </div>
              </div>
              <div className="personality-chart-finder-section personality-chart-finder-section--luv">
                <span className="personality-chart-title-descriptor-label">{yourBlockLabel('LUV SEARCH')}</span>
                <div className="saved-poster-match-tiles personality-chart-finder-luv-tiles">
                  <div
                    role="checkbox"
                    tabIndex={0}
                    aria-checked={finderRomanticLuv}
                    className={`saved-poster-match-tile${finderRomanticLuv ? ' saved-poster-match-tile--selected' : ''}`}
                    onClick={() => onFinderRomanticLuvChange && onFinderRomanticLuvChange(!finderRomanticLuv)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onFinderRomanticLuvChange && onFinderRomanticLuvChange(!finderRomanticLuv);
                      }
                    }}
                  >
                    <span
                      className={`saved-poster-match-tile-checkbox saved-poster-match-checkbox${finderRomanticLuv ? ' checked' : ''}`}
                      aria-hidden="true"
                    >
                      {finderRomanticLuv ? '✓' : ''}
                    </span>
                    <img src={orheartImage} alt="" className="saved-poster-match-tile-icon" />
                    <h4 className="saved-poster-match-tile-title">ROMANCE</h4>
                  </div>
                  <div
                    role="checkbox"
                    tabIndex={0}
                    aria-checked={finderFriendLuv}
                    className={`saved-poster-match-tile${finderFriendLuv ? ' saved-poster-match-tile--selected' : ''}`}
                    onClick={() => onFinderFriendLuvChange && onFinderFriendLuvChange(!finderFriendLuv)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onFinderFriendLuvChange && onFinderFriendLuvChange(!finderFriendLuv);
                      }
                    }}
                  >
                    <span
                      className={`saved-poster-match-tile-checkbox saved-poster-match-checkbox${finderFriendLuv ? ' checked' : ''}`}
                      aria-hidden="true"
                    >
                      {finderFriendLuv ? '✓' : ''}
                    </span>
                    <img src={friendsImage} alt="" className="saved-poster-match-tile-icon" />
                    <h4 className="saved-poster-match-tile-title">FRIENDS</h4>
                  </div>
                </div>
              </div>
              {finderRomanticLuv && (
                <div className="personality-chart-finder-section personality-chart-finder-section--romance-with">
                  <span className="personality-chart-title-descriptor-label">ROMANCE WITH</span>
                  <div className="personality-chart-finder-gender-row personality-chart-finder-romance-with-row">
                    {(['male', 'female', 'nb']).map((g) => {
                      const selected = Array.isArray(finderLookingForGenders) && finderLookingForGenders.includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          className={`personality-chart-finder-gender-btn personality-chart-finder-gender-btn--romance-with${selected ? ' personality-chart-finder-gender-btn--selected' : ''}`}
                          onClick={() => {
                            if (!onFinderLookingForGendersChange) return;
                            const prev = Array.isArray(finderLookingForGenders) ? finderLookingForGenders : [];
                            const next = selected ? prev.filter((x) => x !== g) : [...prev, g];
                            onFinderLookingForGendersChange(next);
                          }}
                          aria-pressed={selected}
                        >
                          <img
                            src={orheartImage}
                            alt=""
                            className="personality-chart-finder-romance-with-heart"
                            aria-hidden
                          />
                          {g === 'male' ? 'MALE' : g === 'female' ? 'FEMALE' : 'NB'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

        {!inline && titleDescriptor != null && (!wordFromJungianAfterConfirm || generatedWordReady) && (
          <button
            type="button"
            className="personality-chart-title-descriptor-confirm"
            disabled={
              wordConfirmed ||
              String(titleDescriptor || '').trim() === '' ||
              (collageFinderEnabled &&
                (!finderSelfGender ||
                  (!finderFriendLuv && !finderRomanticLuv) ||
                  (finderRomanticLuv &&
                    (!Array.isArray(finderLookingForGenders) || finderLookingForGenders.length === 0))))
            }
            onClick={() => {
              if (wordConfirmed) return;
              const trimmed = String(titleDescriptor || '').trim();
              if (trimmed === '') {
                setShowEmptyHint(true);
                setInputShake(true);
                if (descriptorInputRef.current) {
                  descriptorInputRef.current.focus();
                  try { navigator.vibrate && navigator.vibrate([100, 50, 100]); } catch (_) {}
                }
                setTimeout(() => setInputShake(false), 500);
                return;
              }
              if (
                collageFinderEnabled &&
                (!finderSelfGender ||
                  (!finderFriendLuv && !finderRomanticLuv) ||
                  (finderRomanticLuv &&
                    (!Array.isArray(finderLookingForGenders) || finderLookingForGenders.length === 0)))
              ) {
                return;
              }
              setShowEmptyHint(false);
              setWordConfirmed(true);
              setTimeout(() => onClose(), 400);
            }}
            aria-label={wordConfirmed ? 'Confirmed' : 'Confirm and close'}
          >
            {wordConfirmed ? 'Confirmed!' : 'Confirm'}
          </button>
        )}

    </div>
  );

  if (inline) return inner;
  return (
    <div className="personality-chart-overlay">
      {inner}
    </div>
  );
};

export default PersonalityChart;
